type StickyBanner = {
    id: string;
    from: string;
    to: string;
    html: string;
};

declare global {
    interface Window {
        elhcms?: {
            stickyBanners?: StickyBanner[];
        };
    }
}

const cookiePrefix = "elhcms-sticky-banner-";
const containerId = "elhcms-sticky-banner-container";

function isInDateRange(banner: StickyBanner): boolean {
    const now = new Date();
    const from = new Date(banner.from);
    const to = new Date(banner.to);

    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return false;
    }

    return now >= from && now <= to;
}

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function setAcknowledgementCookie(bannerId: string): void {
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();

    document.cookie = `${encodeURIComponent(cookiePrefix + bannerId)}=1; expires=${expires}; path=/; SameSite=Lax`;
}

function hasBeenAcknowledged(bannerId: string): boolean {
    return getCookie(cookiePrefix + bannerId) === "1";
}

function createBannerElement(banner: StickyBanner): HTMLElement | null {
    const wrapper = document.createElement("div");
    wrapper.className = "elhcms-sticky-banner-item";
    wrapper.dataset.stickyBannerId = banner.id;
    wrapper.innerHTML = banner.html.trim();

    const inner = wrapper.querySelector<HTMLElement>(".stickyInner");

    if (inner && !inner.id) {
        inner.id = banner.id;
    }

    return wrapper;
}

function removeBanner(item: HTMLElement, container: HTMLElement): void {
    const bannerId = item.dataset.stickyBannerId;

    if (bannerId) {
        setAcknowledgementCookie(bannerId);
    }

    item.remove();

    if (!container.querySelector(".elhcms-sticky-banner-item")) {
        container.remove();
    }
}

function bindCloseButtons(container: HTMLElement): void {
    container.addEventListener("click", event => {
        const target = event.target as HTMLElement | null;

        if (!target) {
            return;
        }

        const closeButton = target.closest<HTMLElement>(".close-sticky");

        if (!closeButton) {
            return;
        }

        const item = closeButton.closest<HTMLElement>(".elhcms-sticky-banner-item");

        if (!item) {
            return;
        }

        event.preventDefault();
        removeBanner(item, container);
    });
}

function bindScrollBehaviour(container: HTMLElement): void {
    let currentTop = 0;
    let targetTop = 0;
    let lastScrollY = window.scrollY;
    let animationFrameId = 0;

    // Lower values move more slowly and smoothly. Higher values move more quickly.
    const speed = 0.12;

    function getBottomTop(): number {
        return Math.max(0, window.innerHeight - container.offsetHeight);
    }

    function animate(): void {
        currentTop += (targetTop - currentTop) * speed;
        container.style.top = `${currentTop}px`;

        if (Math.abs(targetTop - currentTop) > 0.5) {
            animationFrameId = window.requestAnimationFrame(animate);
            return;
        }

        currentTop = targetTop;
        container.style.top = `${currentTop}px`;
        animationFrameId = 0;
    }

    function moveTo(top: number): void {
        targetTop = Math.max(0, Math.min(top, getBottomTop()));

        if (animationFrameId === 0) {
            animationFrameId = window.requestAnimationFrame(animate);
        }
    }

    function updatePosition(): void {
        const scrollY = Math.max(window.scrollY, 0);
        const bottomTop = getBottomTop();

        if (scrollY <= 0) {
            moveTo(bottomTop);
        } else if (scrollY > lastScrollY) {
            moveTo(0);
        } else if (scrollY < lastScrollY) {
            moveTo(bottomTop);
        }

        lastScrollY = scrollY;
    }

    function resetPosition(): void {
        const bottomTop = getBottomTop();

        currentTop = Math.max(0, Math.min(currentTop, bottomTop));
        targetTop = Math.max(0, Math.min(targetTop, bottomTop));
        container.style.top = `${currentTop}px`;
    }

    currentTop = getBottomTop();
    targetTop = currentTop;

    container.style.top = `${currentTop}px`;
    container.style.bottom = "auto";

    updatePosition();

    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", () => {
        resetPosition();
        updatePosition();
    });
}

function initStickyBanners(): void {
    const banners = window.elhcms?.stickyBanners;

    if (!Array.isArray(banners) || banners.length === 0) {
        return;
    }

    const visibleBanners = banners.filter(banner =>
        banner?.id &&
        banner?.html &&
        isInDateRange(banner) &&
        !hasBeenAcknowledged(banner.id)
    );

    if (visibleBanners.length === 0) {
        return;
    }

    const container = document.createElement("div");
    container.id = containerId;
    container.className = "elhcms-sticky-banner-container";
    container.setAttribute("role", "region");
    container.setAttribute("aria-label", "Site notice");

    visibleBanners.forEach(banner => {
        const element = createBannerElement(banner);

        if (element) {
            container.appendChild(element);
        }
    });

    document.body.appendChild(container);

    bindCloseButtons(container);
    bindScrollBehaviour(container);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStickyBanners);
} else {
    initStickyBanners();
}

export {};